import { useState, useRef, useEffect } from 'react';

/**
 * YouTube player using the IFrame Player API.
 *
 * Bug fix (2026-08-16 v2):
 *  - The YT API REPLACES the element you give it with an iframe. If we put the
 *    ref on the styled wrapper, our visibility toggle gets lost. Solution:
 *    outer wrapper keeps the visibility style; a fresh inner <div> (created
 *    inside the effect so React never reconciles it) is what YT replaces.
 *  - Thumbnails fall back through maxres → sd → hq (some YT uploads don't have
 *    maxresdefault so we cascade to the best available image).
 *  - playVideo() is called synchronously in the onClick handler so mobile
 *    browsers count it as a user gesture (single tap, sound on).
 */

let ytApiPromise = null;
const loadYouTubeAPI = () => {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (ytApiPromise) return ytApiPromise;

  ytApiPromise = new Promise((resolve) => {
    const previous = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof previous === 'function') previous();
      resolve(window.YT);
    };
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    tag.async = true;
    document.body.appendChild(tag);
  });
  return ytApiPromise;
};

// Ordered list of YouTube thumbnail sizes, highest first
const thumbUrls = (id) => [
  `https://i.ytimg.com/vi/${id}/maxresdefault.jpg`, // 1280x720 (not always available)
  `https://i.ytimg.com/vi/${id}/sddefault.jpg`,     // 640x480
  `https://i.ytimg.com/vi/${id}/hqdefault.jpg`,     // 480x360
];

export const VideoPlayer = ({ videoId, ratio = '16:9', title = 'Vidéo', thumbnail }) => {
  const [showThumbnail, setShowThumbnail] = useState(true);
  const [playerReady, setPlayerReady] = useState(false);
  const [thumbIdx, setThumbIdx] = useState(0);
  const wrapperRef = useRef(null);   // outer div — keeps visibility style
  const playerRef = useRef(null);    // YT.Player instance
  const aspect = ratio === '9:16' ? '9 / 16' : '16 / 9';
  const thumbSrc = thumbnail || thumbUrls(videoId)[thumbIdx];

  useEffect(() => {
    let cancelled = false;
    let placeholder;

    loadYouTubeAPI().then((YT) => {
      if (cancelled || !YT || !wrapperRef.current) return;
      // Create a fresh div for YT to replace. React never touches it,
      // so the reconciler can't fight with the API.
      placeholder = document.createElement('div');
      placeholder.style.width = '100%';
      placeholder.style.height = '100%';
      wrapperRef.current.appendChild(placeholder);

      playerRef.current = new YT.Player(placeholder, {
        videoId,
        width: '100%',
        height: '100%',
        playerVars: {
          playsinline: 1,
          rel: 0,
          modestbranding: 1,
          controls: 1,
          enablejsapi: 1,
          cc_load_policy: 0,   // don't force captions on
          hl: 'fr',
          iv_load_policy: 3,   // hide annotations
          vq: 'hd1080',        // request 1080p (best-effort)
        },
        events: {
          onReady: (e) => {
            if (cancelled) return;
            setPlayerReady(true);
            // Best-effort quality preference (YT may downgrade based on bandwidth).
            try { e.target.setPlaybackQuality('hd1080'); } catch (_) { /* noop */ }
            // Force captions OFF (some YT users have CC on by default).
            try { e.target.unloadModule('captions'); } catch (_) { /* noop */ }
            try { e.target.unloadModule('cc'); } catch (_) { /* noop */ }
          },
          onPlaybackQualityChange: (e) => {
            // Nudge back to 1080p if YT drops us below.
            try { e.target.setPlaybackQuality('hd1080'); } catch (_) { /* noop */ }
          },
          onStateChange: (e) => {
            // 1 = playing. Re-assert quality + captions off when playback starts.
            if (e.data === 1) {
              try { e.target.setPlaybackQuality('hd1080'); } catch (_) { /* noop */ }
              try { e.target.unloadModule('captions'); } catch (_) { /* noop */ }
              try { e.target.unloadModule('cc'); } catch (_) { /* noop */ }
            }
          },
        },
      });
    });

    return () => {
      cancelled = true;
      try {
        if (playerRef.current && playerRef.current.destroy) playerRef.current.destroy();
      } catch (_) { /* noop */ }
    };
  }, [videoId]);

  const handlePlay = () => {
    // Synchronous call inside the click handler → user-gesture unlock on iOS/Android.
    if (!playerRef.current || !playerReady) return;
    try {
      playerRef.current.playVideo();
      setShowThumbnail(false);
    } catch (_) { /* noop */ }
  };

  return (
    <div
      className="relative w-full overflow-hidden"
      style={{ aspectRatio: aspect, borderRadius: 12, background: '#000' }}
    >
      {/* Outer wrapper: React-owned, keeps visibility style */}
      <div
        ref={wrapperRef}
        data-testid="video-iframe-wrapper"
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          visibility: showThumbnail ? 'hidden' : 'visible',
        }}
      />

      {showThumbnail && (
        <button
          type="button"
          onClick={handlePlay}
          disabled={!playerReady}
          aria-label={`Lire ${title}`}
          data-testid="video-play-btn"
          className="group absolute inset-0 w-full h-full cursor-pointer"
          style={{
            padding: 0,
            border: 'none',
            background: 'transparent',
            opacity: playerReady ? 1 : 0.9,
          }}
        >
          <img
            src={thumbSrc}
            onError={() => {
              if (!thumbnail && thumbIdx < thumbUrls(videoId).length - 1) {
                setThumbIdx((i) => i + 1);
              }
            }}
            alt=""
            loading="lazy"
            style={{
              position: 'absolute', inset: 0,
              width: '100%', height: '100%',
              objectFit: 'cover', display: 'block',
            }}
          />
          <span
            style={{
              position: 'absolute', inset: 0,
              background: 'rgba(0,0,0,0.25)',
            }}
          />
          <span
            className="transition-transform group-hover:scale-[1.06]"
            style={{
              position: 'absolute',
              top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 72, height: 72,
              borderRadius: '50%',
              background: '#F5EFE3',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <svg
              width="26" height="30" viewBox="0 0 26 30"
              style={{ marginLeft: 3, display: 'block' }}
              aria-hidden="true"
            >
              <polygon points="0,0 26,15 0,30" fill="#2E4A33" />
            </svg>
          </span>
        </button>
      )}
    </div>
  );
};

export default VideoPlayer;
